import { Component, ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import { ObjectWithId } from '@synergy/models';
import { PaginatedResponse } from '@synergy/api';

import { ConfirmationDialog } from '../confirmation-dialog/confirmation-dialog';
import { OverviewTable } from '../overview/overview-table';
import { PoolTableColumn, PoolTableRow } from '../table';

export interface OverviewTableComponentProps<T> {
    data: PaginatedResponse<T>;
    requesting?: boolean;
    selectable?: boolean;
    selected?: number[];
    selectionDisabled?: number[];
    enableHover?: boolean;
    additionalHeader?: ReactNode;
    emptyResultMessage?: ReactNode;
    emptyResultAction?: ReactNode;
    onSelected?: (selected: number[]) => void;
    onItemSelected: (id: number) => void;
    onResetSearch?: () => void;
}

interface OverviewTableState {
    isDeleteWarning: boolean;
    showWarning: boolean;
    // Arbitrary data can be of any type
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data?: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    confirmCallback?: (data?: any) => void;
}

export abstract class OverviewTableComponent<T extends ObjectWithId, P = unknown> extends Component<
    P & OverviewTableComponentProps<T>,
    OverviewTableState
> {
    public override state: OverviewTableState = {
        showWarning: false,
        isDeleteWarning: false,
    };

    protected abstract columns: PoolTableColumn[];

    protected abstract getRows: () => PoolTableRow[];

    protected showWarning<TData>(
        confirmCallback: (data: TData) => void,
        data?: TData,
        isDeleteWarning?: boolean
    ): void {
        this.setState({ showWarning: true, isDeleteWarning: !!isDeleteWarning, confirmCallback, data });
    }

    protected closeWarning = () => {
        this.setState({ showWarning: false });
    };

    protected confirmWarning = () => {
        if (this.state.confirmCallback) {
            this.state.confirmCallback(this.state.data);
        }
        this.closeWarning();
    };

    public override render() {
        return (
            <>
                <OverviewTable
                    data={this.props.data}
                    columns={this.columns}
                    rows={this.getRows()}
                    requesting={this.props.requesting}
                    onItemSelected={this.props.onItemSelected}
                    stickyHeader={true}
                    selectable={this.props.selectable}
                    selected={this.props.selected}
                    onSelected={this.props.onSelected}
                    selectionDisabled={this.props.selectionDisabled}
                    enableHover={this.props.enableHover}
                    additionalHeader={this.props.additionalHeader}
                    onResetSearch={this.props.onResetSearch}
                    emptyResultMessage={this.props.emptyResultMessage}
                    emptyResultAction={this.props.emptyResultAction}
                />
                <ConfirmationDialog
                    isVisible={this.state.showWarning}
                    title={this.state.isDeleteWarning && <FormattedMessage id="app.common.deleteHeader" />}
                    action={this.state.isDeleteWarning && <FormattedMessage id="app.common.delete" />}
                    onConfirm={this.confirmWarning}
                    onCancel={this.closeWarning}
                >
                    {this.state.isDeleteWarning && <FormattedMessage id="app.common.deleteWarning" />}
                </ConfirmationDialog>
            </>
        );
    }
}
